import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, IGroupConfiguration, Group, IGridsterOptions, IValueViewModel, Dashboard, TagStringValueDto, Dictionary, DigitalSetSimpleDictionary } from '../../models';
import { IWidget, IWidgetConfiguration } from '../../widgets';
import { GridsterOptions } from '../gridsterOptions';
import { IRootScope } from '../../IRootScope';
import { IDateRangeService, IStoreService, ILoggerService, IValidationPageConfigurationService, ICurrentApplicationService, IStringValuesService } from '../../services';
import { IEditWidgetStateService } from './editWidgetState.service';
import { backButtonClick, toggleEditMode } from '../../common';
import { StoreElement, Store } from '../../stateManagement';
import { IWidgetContextual } from '../../states';
import { ILayoutWidgets, IWidgetConfigurationService } from '../../services/widgets';

export interface IEditGroupWidgetParams extends angular.ui.IStateParamsService {
    groupGuid: string;
    widgetGuid: string;
    beginDate: string;
    endDate: string;
}

class EditGroupWidgetState {
    applicationGuid: string;
    class: string;
    contextual: IWidgetContextual;
    dashboards: Dashboard[];
    digitalSetDictionary: DigitalSetSimpleDictionary;
    formController: angular.IFormController;
    groupConfig: IGroupConfiguration;
    groupGuid: string;
    groupsDictionary: Dictionary<Group>;
    groups: Group[];
    hasTypes: boolean;
    isBindable: boolean;
    item: IWidget;
    options: IGridsterOptions;
    stringValues: TagStringValueDto[];
    referenceGroupGuid: string;
    viewModels: IValueViewModel[];
    viewModelsDictionary: Dictionary<IValueViewModel>;
    widgets: ILayoutWidgets;

    private _pristineItemConfiguration: IWidgetConfiguration;
    private _storeLoaded: angular.IDeferred<void>;
    private _unsubscribe: (() => void)[] = [];
    private _widgetIndex: number;
    private _widgets: IWidget[];

    static $inject = [
        '$q',
        '$scope',
        '$rootScope',
        '$state',
        '$stateParams',
        '$timeout',
        'storeService',
        'dateRangeService',
        'widgetConfigurationService',
        'loggerService',
        'validationPageConfigurationService',
        'editWidgetStateService',
        'currentApplicationService',
        'stringValuesService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$stateParams: IEditGroupWidgetParams,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _dateRangeService: IDateRangeService,
        private _widgetConfigurationService: IWidgetConfigurationService,
        private _loggerService: ILoggerService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _editWidgetStateService: IEditWidgetStateService,
        private _currentApplicationService: ICurrentApplicationService,
        private _stringValuesService: IStringValuesService
    ) {
        this._storeLoaded = this._$q.defer<void>();
    }

    $onInit() {
        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode);
        }

        this._unsubscribe.push(this._$rootScope.$on(backButtonClick, () => this.leave()));

        if (this._$stateParams.beginDate) {
            this._dateRangeService.beginDate(this._$stateParams.beginDate);
        }
        if (this._$stateParams.endDate) {
            this._dateRangeService.endDate(this._$stateParams.endDate);
        }
        this._dateRangeService.initialized(true);

        this.groupGuid = this._$stateParams.groupGuid;
        // when editing widget, consider this group
        this.referenceGroupGuid = this.groupGuid;
        this.options = new GridsterOptions().options();
        this.applicationGuid = this._currentApplicationService.getApplicationConfiguration()?.guid;

        this._unsubscribe.push(this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.DigitalSets,
                StoreElement.Groups,
                StoreElement.Dashboards,
                StoreElement.ValueViewModels
            ]
        ));

        this._storeLoaded.promise
            .then(() => this._stringValuesService.getAll())
            .then(stringValues => this.stringValues = stringValues)
            .then(() => this._validationPageConfigurationService.get(this.groupGuid, this.viewModelsDictionary, this.groupsDictionary))
            .then(widgets => this.initialLoad(widgets))
            .catch(e => this._loggerService.logError(e));
    }

    $onDestroy() {
        this._unsubscribe.forEach(u => u());
    }

    save(): void {
        this._validationPageConfigurationService
            .update(this.groupGuid, this._widgets)
            .then(() => {
                this.formController?.$setPristine();
                this.forceReload();
                this._loggerService.logSuccess(`Widget ${this.item.name} saved successfully.`);
            })
            .catch(e => {
                this._loggerService.logError(`Error while saving ${this.item.name} .`);
                this._loggerService.logError(e, false);
            });
    }

    cancel(): void {
        this.item.configuration = angular.copy(this._pristineItemConfiguration);
        this._$timeout(() => this.formController.$setPristine());
    }

    private refreshStore(store: Store) {
        this.digitalSetDictionary = store.digitalSetDictionary.dictionary;
        this.groupsDictionary = store.groups.dictionary;
        this.groups = _.values(store.groups.dictionary);
        this.dashboards = _.values(store.dashboards.dictionary);
        this.viewModelsDictionary = store.valueViewModels.dictionary;
        this.viewModels = _.values(store.valueViewModels.dictionary);
        this._storeLoaded.resolve();
    }

    private initialLoad(validationWidgets: IWidget[]) {
        this._widgets = validationWidgets;
        this._widgetIndex = _.findIndex(validationWidgets, w => w.guid === this._$stateParams.widgetGuid);

        const widget = validationWidgets[this._widgetIndex];
        if (!widget) {
            this._loggerService.logError('Cannot find widget to edit');
        }
        widget.data = [];
        this._pristineItemConfiguration = angular.copy(widget.configuration);
        this.item = widget;
        this._$scope.$watch(() => this.item.configuration, (newValue, oldValue) => {
            if (oldValue && newValue !== oldValue && this.formController) {
                this.formController.$setDirty();
            }
        }, true);
        const configuration = this._widgetConfigurationService.getWidgetConfiguration(this.item);

        this.widgets = configuration.widgets;
        this.widgets.right.hideHeaderOptions = true;
        this.contextual = configuration.contextual;
        this.class = configuration.class;
        this.hasTypes = true;
        this.isBindable = true;

        this._editWidgetStateService
            .refreshSeries(this.item, this._$stateParams.beginDate, this._$stateParams.endDate, this.groupGuid)
            .then(data => {
                if (data) {
                    this._$scope.$watch(
                        () => this.item.configuration,
                        () => this._editWidgetStateService.refreshSeries(this.item, this._$stateParams.beginDate, this._$stateParams.endDate, this.groupGuid), true);
                }
            });
    }

    private leave() {
        // one day we may have to make a parameter of validation
        this._$state.go('group.validation', { groupGuid: this.groupGuid });
    }
    
    private forceReload() {
        this._storeService.dispatchers().groups.getAll(true);
        this._storeService.dispatchers().valueViewModels.getAll(true);
    }
}

export const wtEditGroupWidgetState: ComponentDefinition = {
    componentId: 'wtEditGroupWidgetState',
    controller: EditGroupWidgetState,
    template: require('./editWidget.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};